import {
  ArrowRight,
  BadgeCheck,
  BellRing,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleCheck,
  Clock3,
  ExternalLink,
  FileCheck2,
  FileText,
  Globe2,
  HeartHandshake,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  Plane,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  UserRound,
  WandSparkles,
  X,
  Zap,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { apiUrl } from "./api";
import { KNOWLEDGE_BASE_PATH, articlePath, featuredArticles } from "./content/articles";

type ModalName = "roadmap" | "application" | "login" | null;

const jobs = [
  {
    title: "Senior Software Engineer",
    company: "Northstar Health",
    location: "Austin, TX · Hibrit",
    salary: "$145K–$178K",
    visa: "H-1B sponsor geçmişi",
    fit: 94,
    tags: ["React", ".NET", "Sağlık"],
  },
  {
    title: "Data Analyst",
    company: "Cedar Finance",
    location: "New York, NY · Hibrit",
    salary: "$96K–$124K",
    visa: "OPT / H-1B değerlendirilir",
    fit: 87,
    tags: ["SQL", "Python", "Fintech"],
  },
  {
    title: "Mechanical Engineer",
    company: "Orbital Works",
    location: "Seattle, WA · Ofis",
    salary: "$108K–$139K",
    visa: "Relocation + sponsor",
    fit: 82,
    tags: ["CAD", "Üretim", "Savunma dışı"],
  },
];

const visaRows = {
  work: [
    { category: "EB-1", description: "Öncelikli çalışanlar", date: "Güncel", state: "current" },
    { category: "EB-2", description: "İleri derece / özel yetenek", date: "Güncel", state: "current" },
    { category: "EB-3", description: "Nitelikli çalışanlar", date: "01 Eyl 2024", state: "dated" },
    { category: "EB-5", description: "Yatırımcı — ayrılmış", date: "Güncel", state: "current" },
  ],
  family: [
    { category: "F1", description: "ABD vatandaşı bekar çocukları", date: "15 Ara 2018", state: "dated" },
    { category: "F2A", description: "Green Card sahibi eş ve çocukları", date: "22 Tem 2026", state: "dated" },
    { category: "F2B", description: "Green Card sahibi yetişkin çocukları", date: "01 Oca 2018", state: "dated" },
    { category: "F4", description: "ABD vatandaşı kardeşleri", date: "01 Eyl 2009", state: "dated" },
  ],
};

const faqs = [
  {
    q: "Amerikolay resmi bir devlet kurumu mu?",
    a: "Hayır. Amerikolay bağımsız bir hazırlık ve takip platformudur. Resmi kararları ABD Dışişleri Bakanlığı ve USCIS verir. Resmi formlara ve ücretlere her adımda açıkça yönlendiririz.",
  },
  {
    q: "Green Card çekilişine başvuru ücretli mi?",
    a: "Amerikolay'ın kontrol, hatırlatma ve danışman destekli hizmetleri ücretli olabilir. Resmi DV kayıt ücreti ve seçildikten sonraki devlet ücretleri ayrı kalemlerdir; ödeme adımında hangisinin Amerikolay, hangisinin devlet ücreti olduğunu net biçimde gösteririz.",
  },
  {
    q: "Benim adıma iş başvurusu yapıyor musunuz?",
    a: "Plus planda uygun ilanları eşleştirir, CV ve ön yazıyı ilana göre hazırlar, başvuru paketini onayınıza sunarız. Kimliğinizi temsil eden son gönderimler siz onay vermeden yapılmaz.",
  },
  {
    q: "Vize garantisi veriyor musunuz?",
    a: "Hayır. Hiçbir güvenilir hizmet vize, Green Card veya iş garantisi veremez. Biz hata riskini azaltır, süreci görünür kılar ve gerektiğinde yetkili hukuk uzmanlarına yönlendiririz.",
  },
];

const timeline = [
  { label: "Profilin hazır", meta: "8 bilgi doğrulandı", done: true },
  { label: "Uygun yollar eşleşti", meta: "DV + EB-3 + H-1B", done: true },
  { label: "Belge kontrolü", meta: "2 belge bekleniyor", done: false },
  { label: "Başvuru takibi", meta: "Takvime otomatik eklenir", done: false },
];

function BrandMark() {
  return (
    <a className="brand" href="/" aria-label="Amerikolay ana sayfa">
      <span className="brand-orbit" aria-hidden="true">
        a
      </span>
      <span>amerikolay</span>
      <b>.</b>
    </a>
  );
}

export default function AmerikolayHome() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState<ModalName>(null);
  const [activeVisa, setActiveVisa] = useState<"work" | "family">("work");
  const [openFaq, setOpenFaq] = useState(0);
  const [jobQuery, setJobQuery] = useState("");
  const [statusCode, setStatusCode] = useState("");
  const [statusResult, setStatusResult] = useState(false);
  const [quizResult, setQuizResult] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [leadState, setLeadState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const filteredJobs = useMemo(() => {
    const normalized = jobQuery.trim().toLocaleLowerCase("tr");
    if (!normalized) return jobs;
    return jobs.filter((job) =>
      `${job.title} ${job.company} ${job.location} ${job.tags.join(" ")}`
        .toLocaleLowerCase("tr")
        .includes(normalized),
    );
  }, [jobQuery]);

  function closeModal() {
    setModal(null);
    setQuizResult(false);
    setSubmitted(null);
    setApplicationError(null);
    setLeadState("idle");
  }

  async function submitApplication(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setApplicationError(null);
    const data = Object.fromEntries(new FormData(event.currentTarget));

    try {
      const response = await fetch(apiUrl("/api/v1/applications"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Başvuru dosyası oluşturulamadı.");
      }
      const result = (await response.json()) as { referenceCode?: string };
      if (!result.referenceCode) {
        throw new Error("API geçerli bir referans kodu döndürmedi.");
      }
      setSubmitted(result.referenceCode);
    } catch {
      setApplicationError("Şu anda API bağlantısı kurulamıyor. Bilgilerin kaydedilmedi; lütfen biraz sonra tekrar dene.");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLeadState("sending");
    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form));
    try {
      const response = await fetch(apiUrl("/api/v1/leads"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("E-posta kaydı oluşturulamadı.");
      }
      setLeadState("sent");
      form.reset();
    } catch {
      setLeadState("error");
    }
  }

  return (
    <main id="top">
      <header className="site-header">
        <div className="nav-shell">
          <BrandMark />
          <nav className={menuOpen ? "main-nav is-open" : "main-nav"} aria-label="Ana menü">
            <a href="#green-card" onClick={() => setMenuOpen(false)}>Green Card</a>
            <a href="#is-bul" onClick={() => setMenuOpen(false)}>İş bul</a>
            <a href="#vizeler" onClick={() => setMenuOpen(false)}>Vizeler</a>
            <a href="#rehberler" onClick={() => setMenuOpen(false)}>Rehberler</a>
            <a href="#fiyatlar" onClick={() => setMenuOpen(false)}>Üyelik</a>
          </nav>
          <div className="nav-actions">
            <button className="text-button desktop-only" onClick={() => setModal("login")}>Giriş yap</button>
            <button className="button button-sm desktop-only" onClick={() => setModal("roadmap")}>
              Ücretsiz başla <ArrowRight size={16} />
            </button>
            <button
              className="menu-button"
              aria-label={menuOpen ? "Menüyü kapat" : "Menüyü aç"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </header>

      <section className="hero section-shell">
        <div className="hero-copy">
          <a className="update-pill" href="#vize-bulteni">
            <span className="pulse-dot" />
            Ağustos 2026 Vize Bülteni yayında
            <ChevronRight size={15} />
          </a>
          <h1>
            Amerika planın,
            <span>artık daha kolay.</span>
          </h1>
          <p className="hero-lede">
            Hayal etmekle başlar. Doğru planla gerçeğe yaklaşır. Green Card,
            sponsorlu iş ve vize süreçlerini Türkçe ve kişisel bir rotaya dönüştür.
          </p>
          <div className="hero-actions">
            <button className="button button-lg" onClick={() => setModal("roadmap")}>
              Amerika yol haritamı çıkar <WandSparkles size={18} />
            </button>
            <a className="button button-ghost button-lg" href="#nasil-calisir">
              Nasıl çalışır? <ArrowRight size={18} />
            </a>
          </div>
          <div className="trust-list" aria-label="Güven özellikleri">
            <span><ShieldCheck size={17} /> Resmi kaynak takibi</span>
            <span><BellRing size={17} /> Akıllı hatırlatmalar</span>
            <span><LockKeyhole size={17} /> Verilerin güvende</span>
          </div>
        </div>

        <div className="hero-product" aria-label="Amerikolay kişisel panel önizlemesi">
          <div className="hero-halo hero-halo-one" />
          <div className="hero-halo hero-halo-two" />
          <div className="dashboard-card">
            <div className="dashboard-topline">
              <div>
                <p>Günaydın, Deniz 👋</p>
                <h2>Kişisel yol haritan</h2>
              </div>
              <div className="mini-avatar">DA</div>
            </div>
            <div className="progress-block">
              <div className="progress-copy">
                <span>Hazırlık seviyesi</span>
                <strong>%68</strong>
              </div>
              <div className="progress-track"><span /></div>
            </div>
            <div className="timeline-list">
              {timeline.map((item) => (
                <div className="timeline-item" key={item.label}>
                  <div className={item.done ? "timeline-icon done" : "timeline-icon"}>
                    {item.done ? <Check size={14} /> : <Circle size={13} />}
                  </div>
                  <div>
                    <strong>{item.label}</strong>
                    <span>{item.meta}</span>
                  </div>
                  <ChevronRight size={16} />
                </div>
              ))}
            </div>
            <button className="dashboard-action" onClick={() => setModal("roadmap")}>
              Sıradaki adımı gör <ArrowRight size={16} />
            </button>
          </div>
          <div className="floating-card floating-jobs">
            <BriefcaseBusiness size={19} />
            <div><strong>34 yeni iş</strong><span>Profiline uygun</span></div>
          </div>
          <div className="floating-card floating-visa">
            <RefreshCw size={17} />
            <div><strong>Vize bülteni</strong><span>Bugün kontrol edildi</span></div>
          </div>
        </div>
      </section>

      <section className="confidence-bar" aria-label="Platform özellikleri">
        <div className="section-shell confidence-inner">
          <span><BadgeCheck /> Resmi kaynaklardan güncel bilgi</span>
          <span><Clock3 /> 7/24 otomatik takip</span>
          <span><HeartHandshake /> Türkçe insan desteği</span>
          <span><ShieldCheck /> Şeffaf ücret politikası</span>
        </div>
      </section>

      <section className="section-shell product-intro" id="nasil-calisir">
        <div className="eyebrow"><Sparkles size={15} /> Tek platform, net yol haritası</div>
        <div className="section-heading split-heading">
          <h2>Büyük bir hayali<br /><em>net adımlara böldük.</em></h2>
          <p>Yeni bir ülkede yeni bir hayat planlamak cesaret ister. Amerikolay, sana uygun seçenekleri gösterir; son tarihleri, belgeleri ve sıradaki adımı takip eder.</p>
        </div>
        <div className="feature-grid">
          <article className="feature-card feature-green" id="green-card">
            <div className="feature-icon"><FileCheck2 /></div>
            <span className="feature-kicker">Green Card</span>
            <h3>Başvurunu hatasız hazırla, tek yerden takip et.</h3>
            <p>Uygunluk kontrolü, aile bilgileri, fotoğraf ölçümü ve son gönderim öncesi iki aşamalı kontrol.</p>
            <ul>
              <li><CheckCircle2 /> Akıllı form kontrolü</li>
              <li><CheckCircle2 /> 600×600 fotoğraf denetimi</li>
              <li><CheckCircle2 /> Confirmation Number kasası</li>
            </ul>
            <button className="card-link" onClick={() => setModal("application")}>
              Başvuru hazırlığına başla <ArrowRight />
            </button>
            <div className="card-visual photo-checker">
              <div className="photo-frame"><UserRound /></div>
              <div className="photo-copy">
                <span><CircleCheck /> Fotoğraf uygun</span>
                <small>600 × 600 px · JPEG · 184 KB</small>
              </div>
            </div>
          </article>

          <article className="feature-card feature-blue" id="is-bul">
            <div className="feature-icon"><BriefcaseBusiness /></div>
            <span className="feature-kicker">Amerika&apos;da iş</span>
            <h3>Sponsor olabilecek işverenlere daha akıllı başvur.</h3>
            <p>Geçmiş sponsor verisi, ilan uyumu ve ABD formatında CV araçları tek kariyer asistanında.</p>
            <ul>
              <li><CheckCircle2 /> Sponsor geçmişi filtresi</li>
              <li><CheckCircle2 /> CV ve ön yazı uyarlama</li>
              <li><CheckCircle2 /> Başvuru takip panosu</li>
            </ul>
            <a className="card-link" href="#is-araci">İş aracını dene <ArrowRight /></a>
            <div className="card-visual match-visual">
              <div className="match-score"><strong>94</strong><span>% eşleşme</span></div>
              <div><strong>Senior Software Engineer</strong><small>Austin · H-1B geçmişi var</small></div>
            </div>
          </article>

          <article className="feature-card feature-peach" id="vizeler">
            <div className="feature-icon"><Plane /></div>
            <span className="feature-kicker">Vize rehberi</span>
            <h3>Hangi vize sana uygun? Kısa yoldan öğren.</h3>
            <p>Göçmen ve geçici vizeleri sade Türkçe ile karşılaştır; şartları, ücretleri ve bekleme adımlarını gör.</p>
            <ul>
              <li><CheckCircle2 /> 20+ vize türü</li>
              <li><CheckCircle2 /> Kişisel uygunluk ağacı</li>
              <li><CheckCircle2 /> Aylık bülten alarmı</li>
            </ul>
            <button className="card-link" onClick={() => setModal("roadmap")}>Vize eşleştiriciyi aç <ArrowRight /></button>
            <div className="card-visual visa-chips">
              <span>H-1B <b>Güçlü</b></span>
              <span>O-1 <b>İncele</b></span>
              <span>EB-2 NIW <b>Orta</b></span>
            </div>
          </article>
        </div>
      </section>

      <section className="knowledge-preview" id="rehberler">
        <div className="section-shell">
          <div className="eyebrow"><BookOpen size={15} /> Resmi kaynaklara dayanan Türkçe rehberler</div>
          <div className="section-heading split-heading knowledge-preview-heading">
            <h2>Doğru soruyla başla.<br /><em>Net bir cevapla ilerle.</em></h2>
            <div>
              <p>Green Card, Amerika&apos;da iş ve vize süreçleri için kontrol listeleri, kritik uyarılar ve resmi kaynak bağlantıları.</p>
              <a href={KNOWLEDGE_BASE_PATH}>Tüm bilgi merkezini keşfet <ArrowRight size={16} /></a>
            </div>
          </div>
          <div className="knowledge-preview-grid">
            {featuredArticles.map((article, index) => (
              <article className={`knowledge-preview-card tone-${article.tone}`} key={article.slug}>
                <div><span>{String(index + 1).padStart(2, "0")}</span><span><Clock3 size={13} /> {article.readMinutes} dk</span></div>
                <small>{article.category}</small>
                <h3>{article.shortTitle}</h3>
                <p>{article.description}</p>
                <a href={articlePath(article)}>Rehberi oku <ArrowRight size={16} /></a>
              </article>
            ))}
          </div>
          <div className="knowledge-trust-note"><ShieldCheck size={18} /><span><strong>Güven ilkesi:</strong> Her içerikte son inceleme tarihi, birincil resmi kaynaklar ve hukuki tavsiye olmadığına dair açık bilgilendirme bulunur.</span></div>
        </div>
      </section>

      <section className="dark-section" id="is-araci">
        <div className="section-shell">
          <div className="eyebrow eyebrow-dark"><Zap size={15} /> Amerikolay İş Radarı</div>
          <div className="section-heading split-heading dark-heading">
            <h2>Her ilan değil.<br /><em>Sana açık olanlar.</em></h2>
            <p>İlanları sadece unvana göre değil; sponsor geçmişi, vize dili, konum, maaş ve profil uyumuna göre sıralıyoruz.</p>
          </div>
          <div className="jobs-workbench">
            <div className="jobs-sidebar">
              <div className="sidebar-head">
                <span className="status-dot" />
                İş arama profilin aktif
              </div>
              <div className="profile-score">
                <div className="score-ring"><strong>82</strong><span>/100</span></div>
                <div><b>ABD CV puanın</b><small>3 iyileştirme önerisi</small></div>
              </div>
              <div className="check-stack">
                <span><CircleCheck /> Unvan eşleşmesi</span>
                <span><CircleCheck /> İngilizce seviye</span>
                <span className="muted-check"><Circle /> Maaş beklentisi</span>
                <span className="muted-check"><Circle /> Portföy bağlantısı</span>
              </div>
              <button className="button button-light">CV&apos;mi güçlendir <Sparkles size={16} /></button>
            </div>
            <div className="jobs-main">
              <div className="job-toolbar">
                <div className="search-field">
                  <Search size={18} />
                  <input
                    value={jobQuery}
                    onChange={(event) => setJobQuery(event.target.value)}
                    placeholder="Unvan, beceri veya şehir ara"
                    aria-label="İş ilanlarında ara"
                  />
                </div>
                <button className="filter-button">Sponsor: Evet <ChevronDown size={15} /></button>
              </div>
              <div className="job-list">
                {filteredJobs.length ? filteredJobs.map((job) => (
                  <article className="job-row" key={job.title}>
                    <div className="company-logo">{job.company.slice(0, 1)}</div>
                    <div className="job-details">
                      <div className="job-title-line"><h3>{job.title}</h3><span>{job.fit}% uyum</span></div>
                      <p>{job.company} <i /> <MapPin size={13} /> {job.location}</p>
                      <div className="job-tags">{job.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
                    </div>
                    <div className="job-meta">
                      <strong>{job.salary}</strong>
                      <span><BadgeCheck size={14} /> {job.visa}</span>
                      <button aria-label={`${job.title} ilanını aç`}><ArrowRight /></button>
                    </div>
                  </article>
                )) : <div className="empty-jobs">Bu aramayla eşleşen örnek ilan yok.</div>}
              </div>
              <div className="jobs-note">
                <ShieldCheck size={16} /> “Sponsor geçmişi” vize garantisi değildir; işverenlerin kamuya açık geçmiş beyanlarına dayanır.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell bulletin-section" id="vize-bulteni">
        <div className="bulletin-copy">
          <div className="eyebrow"><RefreshCw size={15} /> Resmi kaynaklar otomatik izleniyor</div>
          <h2>Vize öncelik tarihin<br />değiştiğinde <em>ilk sen bil.</em></h2>
          <p>ABD Dışişleri Bakanlığı&apos;nın aylık Vize Bülteni sadeleştirilir, profilinle eşleştirilir ve hareket olduğunda sana bildirilir.</p>
          <div className="bulletin-benefits">
            <span><BellRing /> Kategoriye özel alarm</span>
            <span><CalendarDays /> Aylık değişim geçmişi</span>
            <span><Globe2 /> Resmi sayfaya doğrudan bağlantı</span>
          </div>
          <a className="button button-outline" href="https://travel.state.gov/content/travel/en/legal/visa-law0/visa-bulletin.html" target="_blank" rel="noreferrer">
            Resmi bülteni aç <ExternalLink size={16} />
          </a>
        </div>
        <div className="bulletin-board">
          <div className="bulletin-head">
            <div><span className="live-badge"><i /> CANLI TAKİP</span><h3>Ağustos 2026</h3></div>
            <div className="last-checked"><RefreshCw /> Son kontrol<br /><strong>14 Ağu · 06:00</strong></div>
          </div>
          <div className="tab-row" role="tablist" aria-label="Vize bülteni kategorileri">
            <button className={activeVisa === "work" ? "active" : ""} onClick={() => setActiveVisa("work")}>İstihdam bazlı</button>
            <button className={activeVisa === "family" ? "active" : ""} onClick={() => setActiveVisa("family")}>Aile bazlı</button>
          </div>
          <div className="visa-table">
            <div className="visa-table-head"><span>Kategori</span><span>Final Action Date</span></div>
            {visaRows[activeVisa].map((row) => (
              <div className="visa-row" key={row.category}>
                <div><strong>{row.category}</strong><span>{row.description}</span></div>
                <b className={row.state === "current" ? "current" : ""}>{row.date}</b>
              </div>
            ))}
          </div>
          <p className="bulletin-footnote">“Diğer tüm ülkeler” sütunu, Final Action Dates. Türkiye doğumlular için genel görünüm. Kişisel hukuki değerlendirme değildir.</p>
        </div>
      </section>

      <section className="tracking-section">
        <div className="section-shell tracking-grid">
          <div>
            <div className="eyebrow eyebrow-light"><FileText size={15} /> Başvuru takip merkezi</div>
            <h2>“Şimdi ne olacak?”<br />sorusunu ortadan kaldır.</h2>
            <p>Amerikolay referans kodunla hazırlık dosyanın durumunu, eksiklerini ve sıradaki adımı güvenle kontrol et.</p>
          </div>
          <div className="tracking-card">
            <label htmlFor="tracking-code">Amerikolay referans kodu</label>
            <div className="tracking-input">
              <input id="tracking-code" value={statusCode} onChange={(event) => { setStatusCode(event.target.value); setStatusResult(false); }} placeholder="Örn. AK-2026-1842" />
              <button onClick={() => setStatusResult(Boolean(statusCode.trim()))}>Kontrol et <ArrowRight /></button>
            </div>
            {statusResult ? (
              <div className="status-result">
                <div className="status-result-icon"><CheckCircle2 /></div>
                <div><span>Dosya durumu</span><strong>Ön kontrol tamamlandı</strong><small>Sıradaki adım: Fotoğraf doğrulama</small></div>
                <ChevronRight />
              </div>
            ) : (
              <p><LockKeyhole size={14} /> Yalnızca Amerikolay hazırlık dosyalarını gösterir. Resmi USCIS/DV durum sorgusu değildir.</p>
            )}
          </div>
        </div>
      </section>

      <section className="section-shell pricing-section" id="fiyatlar">
        <div className="section-heading centered-heading">
          <div className="eyebrow"><Star size={15} /> İhtiyacın kadar destek</div>
          <h2>Ücretsiz başla.<br /><em>Hazır olduğunda hızlan.</em></h2>
          <p>Bilgiye erişim ücretsiz kalır. Zaman kazandıran otomasyon, uzman kontrolü ve bire bir destek için yükselt.</p>
        </div>
        <div className="pricing-grid">
          <article className="price-card">
            <span className="plan-name">Keşfet</span>
            <div className="price"><strong>₺0</strong><span>sonsuza kadar</span></div>
            <p>Kendi yolunu araştıranlar için güvenilir başlangıç.</p>
            <ul>
              <li><Check /> Kişisel vize eşleştirme</li>
              <li><Check /> Vize rehberleri ve bülten</li>
              <li><Check /> 3 sponsorlu iş alarmı</li>
              <li><Check /> Green Card takvimi</li>
            </ul>
            <button className="button button-outline" onClick={() => setModal("roadmap")}>Ücretsiz başla</button>
          </article>
          <article className="price-card featured-price">
            <div className="popular-label">EN ÇOK TERCİH EDİLEN</div>
            <span className="plan-name">Amerikolay Plus</span>
            <div className="price"><strong>₺449</strong><span>/ ay</span></div>
            <p>İş ve vize sürecini sistemli yürütmek isteyenler için.</p>
            <ul>
              <li><Check /> Sınırsız sponsorlu iş eşleşmesi</li>
              <li><Check /> Her ilana özel CV + ön yazı</li>
              <li><Check /> Akıllı başvuru takipçisi</li>
              <li><Check /> Öncelik tarihi ve son gün alarmları</li>
              <li><Check /> Aylık uzman soru-cevap oturumu</li>
            </ul>
            <button className="button" onClick={() => setModal("login")}>Plus&apos;ı dene <ArrowRight size={16} /></button>
          </article>
          <article className="price-card concierge-price">
            <span className="plan-name">Başvuru Desteği</span>
            <div className="price"><strong>₺1.490</strong><span>/ dosya</span></div>
            <p>Green Card hazırlığını gözden kaçırmadan tamamlamak için.</p>
            <ul>
              <li><Check /> Baştan sona form asistanı</li>
              <li><Check /> Fotoğraf ve bilgi kontrolü</li>
              <li><Check /> Çift kontrol + gönderim provası</li>
              <li><Check /> Sonuç ve seçilme sonrası rehber</li>
            </ul>
            <button className="button button-outline" onClick={() => setModal("application")}>Dosya oluştur</button>
          </article>
        </div>
        <p className="pricing-disclaimer"><ShieldCheck size={15} /> Fiyatlar Amerikolay hizmet bedelidir. Devlet ücretleri varsa ödeme öncesinde ayrı gösterilir. Hukuki temsil dahil değildir.</p>
      </section>

      <section className="section-shell faq-section">
        <div className="faq-title">
          <div className="eyebrow">Sık sorulanlar</div>
          <h2>Aklında soru<br />kalmasın.</h2>
          <p>Daha fazlası için Türkçe destek ekibimize yazabilirsin.</p>
          <a href="mailto:merhaba@amerikolay.com">merhaba@amerikolay.com <ArrowRight size={15} /></a>
        </div>
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div className={openFaq === index ? "faq-item open" : "faq-item"} key={faq.q}>
              <button onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}>
                <span>{faq.q}</span><ChevronDown />
              </button>
              <div className="faq-answer"><p>{faq.a}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="final-cta">
        <div className="section-shell final-cta-inner">
          <div>
            <span className="cta-spark"><Sparkles /></span>
            <h2>Yeni hayatına giden<br /><em>ilk adımı bugün at.</em></h2>
            <p>3 dakikalık sorularla Amerika hedefin için sana uygun yolları ve sıradaki adımını ücretsiz gör.</p>
          </div>
          <form className="cta-form" onSubmit={submitLead}>
            <div><Mail size={18} /><input required name="email" type="email" placeholder="E-posta adresin" aria-label="E-posta adresin" /></div>
            <input type="hidden" name="intent" value="roadmap" />
            <button className="button button-light" disabled={leadState === "sending" || leadState === "sent"}>
              {leadState === "sending" ? <Loader2 className="spin" /> : leadState === "sent" ? <>Kaydın alındı <Check /></> : leadState === "error" ? <>Tekrar dene <RefreshCw /></> : <>Amerika yol haritamı çıkar <ArrowRight /></>}
            </button>
            <small role={leadState === "error" ? "alert" : undefined}>
              {leadState === "error" ? "API bağlantısı kurulamadı; e-posta adresin kaydedilmedi." : "Kaydolarak kullanım koşullarını ve gizlilik politikasını kabul edersin."}
            </small>
          </form>
        </div>
      </section>

      <footer>
        <div className="section-shell footer-grid">
          <div className="footer-brand"><BrandMark /><p>Amerika&apos;ya giden yolda<br />Türkçe yol arkadaşın.</p></div>
          <div><strong>Keşfet</strong><a href="#green-card">Green Card</a><a href="#is-bul">Amerika&apos;da iş</a><a href="#vizeler">Vize rehberi</a><a href="#vize-bulteni">Vize bülteni</a></div>
          <div><strong>Kaynaklar</strong><a href="https://travel.state.gov" target="_blank" rel="noreferrer">travel.state.gov</a><a href="https://uscis.gov" target="_blank" rel="noreferrer">USCIS</a><a href={KNOWLEDGE_BASE_PATH}>Bilgi merkezi</a><a href={articlePath(featuredArticles[2])}>Vize sözlüğü</a></div>
          <div><strong>Amerikolay</strong><a href="#top">Hakkımızda</a><a href="#top">Uzman ağı</a><a href="/rehberler/gocmenlik-dolandiriciliklarindan-korunma/">Güvenlik</a><a href="mailto:merhaba@amerikolay.com">İletişim</a></div>
        </div>
        <div className="section-shell footer-bottom">
          <span>© 2026 Amerikolay. Tüm hakları saklıdır.</span>
          <div><a href="#top">Gizlilik</a><a href="#top">Koşullar</a><a href="#top">Çerezler</a></div>
          <p>Amerikolay bir hukuk bürosu veya devlet kurumu değildir.</p>
        </div>
      </footer>

      {modal && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeModal()}>
          <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <button className="modal-close" onClick={closeModal} aria-label="Pencereyi kapat"><X /></button>
            {modal === "roadmap" && (
              <div className="modal-content">
                <div className="modal-icon"><Sparkles /></div>
                {!quizResult ? <>
                  <span className="modal-step">ÜCRETSİZ · 3 DAKİKA</span>
                  <h2 id="modal-title">Sana uygun Amerika yollarını bulalım.</h2>
                  <p>Bu ön değerlendirme hukuki tavsiye değildir; seçenekleri daha hızlı keşfetmeni sağlar.</p>
                  <form onSubmit={(event) => { event.preventDefault(); setQuizResult(true); }} className="modal-form">
                    <label>Öncelikli hedefin<select required defaultValue=""><option value="" disabled>Bir hedef seç</option><option>Amerika&apos;da çalışmak</option><option>Green Card çekilişi</option><option>Eğitim almak</option><option>Ailemin yanına gitmek</option><option>Yatırım / şirket kurmak</option></select></label>
                    <label>En yüksek eğitim seviyen<select required defaultValue=""><option value="" disabled>Seç</option><option>Lise</option><option>Ön lisans</option><option>Lisans</option><option>Yüksek lisans / Doktora</option></select></label>
                    <label>Mesleki deneyimin<select required defaultValue=""><option value="" disabled>Seç</option><option>Henüz yok</option><option>1–3 yıl</option><option>4–7 yıl</option><option>8+ yıl</option></select></label>
                    <button className="button button-lg">Eşleşmelerimi göster <ArrowRight /></button>
                  </form>
                </> : <div className="quiz-result">
                  <span className="modal-step">İLK EŞLEŞMEN HAZIR</span>
                  <h2 id="modal-title">Senin için 3 güçlü yol bulduk.</h2>
                  <p>Profil tamamlandıkça uygunluk ve öncelik sırası daha isabetli hale gelir.</p>
                  <div className="result-path"><b>01</b><div><strong>DV Green Card</strong><span>Başvuru dönemi için hazırlık yap</span></div><em>Yüksek uyum</em></div>
                  <div className="result-path"><b>02</b><div><strong>H-1B Sponsorlu İş</strong><span>Meslek + eğitim uyumu güçlü</span></div><em>İncele</em></div>
                  <div className="result-path"><b>03</b><div><strong>EB-2 NIW</strong><span>Deneyim kanıtlarını değerlendir</span></div><em>Orta uyum</em></div>
                  <button className="button button-lg" onClick={() => setModal("login")}>Yol haritamı kaydet <ArrowRight /></button>
                </div>}
              </div>
            )}

            {modal === "application" && (
              <div className="modal-content">
                <div className="modal-icon"><FileCheck2 /></div>
                {!submitted ? <>
                  <span className="modal-step">GREEN CARD HAZIRLIK DOSYASI</span>
                  <h2 id="modal-title">Başvurunu güvenli bir kontrol listesiyle başlat.</h2>
                  <p>Önce temel uygunluk bilgilerini alırız. Resmi gönderim öncesinde tüm bilgiler sana gösterilir ve onayın istenir.</p>
                  <form onSubmit={submitApplication} className="modal-form two-column-form">
                    <label>Ad soyad<input name="fullName" required placeholder="Pasaporttaki gibi" /></label>
                    <label>E-posta<input name="email" required type="email" placeholder="sen@eposta.com" /></label>
                    <label>Doğduğun ülke<select name="birthCountry" required defaultValue="Türkiye"><option>Türkiye</option><option>Diğer</option></select></label>
                    <label>Eğitim<select name="education" required defaultValue=""><option value="" disabled>Seç</option><option>Lise</option><option>Ön lisans</option><option>Lisans</option><option>Yüksek lisans / Doktora</option></select></label>
                    <label className="consent-label"><input type="checkbox" required /><span>Bilgilerin doğru olduğunu ve bunun resmi DV başvurusu değil, Amerikolay hazırlık dosyası olduğunu anlıyorum.</span></label>
                    {applicationError && <p className="form-error" role="alert">{applicationError}</p>}
                    <button className="button button-lg" disabled={submitting}>{submitting ? <><Loader2 className="spin" /> Dosya oluşturuluyor</> : <>Hazırlık dosyamı oluştur <ArrowRight /></>}</button>
                  </form>
                </> : <div className="submission-success">
                  <div className="success-check"><Check /></div>
                  <span className="modal-step">DOSYAN OLUŞTURULDU</span>
                  <h2 id="modal-title">Harika bir başlangıç.</h2>
                  <p>Bu referans kodunu sakla. Fotoğraf ve aile bilgileri adımına profilinden devam edebilirsin.</p>
                  <div className="reference-code">{submitted}</div>
                  <button className="button button-lg" onClick={closeModal}>Tamam <Check /></button>
                </div>}
              </div>
            )}

            {modal === "login" && (
              <div className="modal-content login-content">
                <div className="modal-icon"><UserRound /></div>
                <span className="modal-step">HESABIN</span>
                <h2 id="modal-title">Yol haritana devam et.</h2>
                <p>Profilini, başvurularını ve son tarihlerini tek yerde güvenle takip et.</p>
                <form className="modal-form" onSubmit={submitLead}>
                  <label>E-posta<input required type="email" name="email" placeholder="sen@eposta.com" /></label>
                  <button className="button button-lg" disabled={leadState === "sending"}>
                    {leadState === "sending" ? <Loader2 className="spinner" /> : <Send />}
                    {leadState === "sending" ? "Gönderiliyor..." : "Güvenli bağlantı gönder"}
                  </button>
                </form>
                {leadState === "sent" && <p className="success-message">✓ Giriş bağlantısı e-postana gönderildi!</p>}
                {leadState === "error" && <p className="error-message">✗ Hata oluştu. Lütfen tekrar dene.</p>}
                <small className="login-note"><LockKeyhole /> Şifresiz giriş bağlantısı 10 dakika geçerlidir.</small>
              </div>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
